import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { 
    FileText, Plus, Send, CheckCircle, Clock, Banknote, AlertTriangle,
    Zap, HardHat, Filter, Eye, Loader2, AlertCircle, ArrowUpRight, ArrowDownLeft,
    Building2, User, Users, Download, Settings, UserCheck, XCircle, Bolt, History, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const STATUS_CONFIG = {
    brouillon: { label: 'Brouillon', color: 'bg-slate-100 text-slate-700', icon: Clock },
    en_verification: { label: 'En vérification', color: 'bg-amber-100 text-amber-700', icon: AlertTriangle },
    validee: { label: 'Validée', color: 'bg-blue-100 text-blue-700', icon: UserCheck },
    emise: { label: 'Émise', color: 'bg-indigo-100 text-indigo-700', icon: Send },
    payee: { label: 'Payée', color: 'bg-green-100 text-green-700', icon: CheckCircle },
    rejetee: { label: 'Rejetée', color: 'bg-red-100 text-red-700', icon: XCircle }
};

const TVA_OPTIONS = [
    { value: 'tva_20', label: 'TVA 20%', rate: 20 },
    { value: 'tva_10', label: 'TVA 10%', rate: 10 },
    { value: 'tva_5_5', label: 'TVA 5.5%', rate: 5.5 },
    { value: 'exonere', label: 'Exonéré (art. 293B CGI)', rate: 0 }
];

const PAYER_OPTIONS = [
    { value: 'valnexo', label: 'Valnexo' },
    { value: 'at_service', label: 'AT Service' },
    { value: 'vp', label: 'VP (pour apporteurs)' }
];

export default function BillingV2() {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [invoices, setInvoices] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [billingConfigs, setBillingConfigs] = useState([]);
    const [accountantAccesses, setAccountantAccesses] = useState([]);
    const [forceHistory, setForceHistory] = useState([]);
    const [availablePayers, setAvailablePayers] = useState([]); // Liste des payeurs
    const [activeTab, setActiveTab] = useState('invoices');
    
    // Dialogs
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [showConfigDialog, setShowConfigDialog] = useState(false);
    const [showAccountantDialog, setShowAccountantDialog] = useState(false);
    const [showForceDialog, setShowForceDialog] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [selectedConfig, setSelectedConfig] = useState(null);
    const [selectedForce, setSelectedForce] = useState(null);
    
    // Create invoice form
    const [createForm, setCreateForm] = useState({
        issuer_type: '',
        payer_type: 'valnexo',
        invoice_number: '',
        tva_regime: 'tva_20',
        notes: ''
    });
    const [suggestedNumber, setSuggestedNumber] = useState('');
    
    // Accountant access form
    const [accountantForm, setAccountantForm] = useState({
        email: '',
        password: '',
        name: ''
    });
    
    // Force invoice form
    const [forceForm, setForceForm] = useState({
        reason: '',
        confirm: false
    });
    const [forceLoading, setForceLoading] = useState(false);
    
    // Invoice view dialog
    const [showInvoiceViewDialog, setShowInvoiceViewDialog] = useState(false);
    
    const isAdminFull = (user?.role === 'admin_full' || user?.is_admin === true);
    const isComptable = user?.role === 'comptable';

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [invoicesRes, suggestionsRes, payersRes] = await Promise.all([
                api.get('/invoices/v2'),
                api.get('/billing/suggestions'),
                api.get('/payers')
            ]);
            setInvoices(invoicesRes.data || []);
            setSuggestions(suggestionsRes.data || []);
            setAvailablePayers(payersRes.data || []);
            
            if (isAdminFull) {
                const [configsRes, accessesRes, historyRes] = await Promise.all([
                    api.get('/billing/configs'),
                    api.get('/accountant-access/all'),
                    api.get('/billing/force-invoice/history')
                ]);
                setBillingConfigs(configsRes.data || []);
                setAccountantAccesses(accessesRes.data || []);
                setForceHistory(historyRes.data || []);
            } else {
                const accessesRes = await api.get('/accountant-access');
                setAccountantAccesses(accessesRes.data || []);
            }
        } catch (error) {
            console.error('Error loading billing data:', error);
            toast.error('Erreur lors du chargement des données');
        } finally {
            setLoading(false);
        }
    };

    const loadSuggestedNumber = async () => {
        try {
            const res = await api.get('/billing/suggest-number');
            setSuggestedNumber(res.data.suggestion);
            setCreateForm(prev => ({ ...prev, invoice_number: res.data.suggestion }));
        } catch (error) {
            console.error('Error loading suggested number:', error);
        }
    };

    const handleSubmitForVerification = async (invoiceId) => {
        try {
            await api.put(`/invoices/v2/${invoiceId}/submit`);
            toast.success('Facture soumise pour vérification');
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };

    const handleValidateInvoice = async (invoiceId) => {
        try {
            await api.put(`/invoices/v2/${invoiceId}/validate`);
            toast.success('Facture validée');
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };

    const handleRejectInvoice = async (invoiceId) => {
        const reason = prompt('Motif du rejet:');
        if (!reason) return;
        
        try {
            await api.put(`/invoices/v2/${invoiceId}/reject`, { reason });
            toast.success('Facture rejetée');
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };

    const handleIssueInvoice = async (invoiceId) => {
        try {
            await api.put(`/invoices/v2/${invoiceId}/issue`);
            toast.success('Facture émise');
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };

    const handleMarkPaid = async (invoiceId) => {
        try {
            await api.put(`/invoices/v2/${invoiceId}/mark-paid`);
            toast.success('Facture marquée comme payée');
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };

    const handleUpdateBillingConfig = async (userId, config) => {
        try {
            await api.put(`/billing/config/${userId}`, config);
            toast.success('Configuration mise à jour');
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };

    const handleCreateAccountantAccess = async () => {
        if (!accountantForm.email || !accountantForm.password || !accountantForm.name) {
            toast.error('Tous les champs sont obligatoires');
            return;
        }
        
        try {
            await api.post('/accountant-access', accountantForm);
            toast.success('Accès comptable créé');
            setShowAccountantDialog(false);
            setAccountantForm({ email: '', password: '', name: '' });
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };

    const handleToggleAccountantAccess = async (accessId) => {
        try {
            await api.put(`/accountant-access/${accessId}/toggle`);
            toast.success('Accès modifié');
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };

    const handleCreateFromSuggestion = async (suggestion, selectedPayer = null, tvaOverride = null) => {
        try {
            const payload = {
                prospect_id: suggestion.prospect_id,
                pipeline_type: suggestion.pipeline_type,
                issuer_type: suggestion.issuer_type,
                issuer_user_id: suggestion.issuer_user_id,
                payer_type: selectedPayer || suggestion.payer_type,
                amount: suggestion.amount,
                invoice_label: suggestion.invoice_label,
                tva_regime_override: tvaOverride
            };
            
            const res = await api.post('/invoices/v2/create-from-suggestion', payload);
            toast.success(`Facture ${res.data.invoice_number} créée`);
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la création');
        }
    };
    
    // Stocker les payeurs sélectionnés par suggestion
    const [selectedPayers, setSelectedPayers] = useState({});
    // Stocker les régimes TVA sélectionnés par suggestion
    const [selectedTvas, setSelectedTvas] = useState({});
    
    const handlePayerChange = (suggestionKey, newPayer) => {
        setSelectedPayers(prev => ({
            ...prev,
            [suggestionKey]: newPayer
        }));
    };
    
    const handleTvaChange = (suggestionKey, newTva) => {
        setSelectedTvas(prev => ({
            ...prev,
            [suggestionKey]: newTva
        }));
    };
    
    const getSuggestionKey = (s, idx) => `${s.prospect_id}_${s.pipeline_type}_${s.issuer_type}_${idx}`;

    const handleOpenForceDialog = (suggestion) => {
        setSelectedForce(suggestion);
        setForceForm({ reason: '', confirm: false });
        setShowForceDialog(true);
    };

    const handleForceInvoice = async () => {
        if (!forceForm.reason || forceForm.reason.length < 10) {
            toast.error('La raison doit contenir au moins 10 caractères');
            return;
        }
        if (!forceForm.confirm) {
            toast.error('Veuillez confirmer l\'action de forçage');
            return;
        }

        setForceLoading(true);
        try {
            const res = await api.post('/billing/force-invoice', {
                prospect_id: selectedForce.prospect_id,
                pipeline_type: selectedForce.pipeline_type,
                reason: forceForm.reason,
                confirm: true
            });
            toast.success(`Facturation forcée - Facture ${res.data.invoice_number} créée`);
            setShowForceDialog(false);
            setSelectedForce(null);
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors du forçage');
        } finally {
            setForceLoading(false);
        }
    };

    // Mise à jour du statut de facture (workflow)
    const handleUpdateInvoiceStatus = async (invoiceId, newStatus) => {
        try {
            await api.put(`/invoices/v2/${invoiceId}/status`, { status: newStatus });
            const statusLabels = {
                'en_verification': 'soumise pour vérification',
                'validee': 'validée',
                'emise': 'émise',
                'payee': 'marquée comme payée'
            };
            toast.success(`Facture ${statusLabels[newStatus] || newStatus}`);
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
        }
    };

    // Télécharger le PDF
    const handleDownloadPDF = async (invoiceId) => {
        try {
            const response = await api.get(`/invoices/v2/${invoiceId}/pdf`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `facture_${invoiceId}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            toast.error('Erreur lors du téléchargement du PDF');
        }
    };

    const handleDownloadPdf = async (invoiceId, invoiceNumber) => {
        try {
            const response = await api.get(`/invoices/v2/${invoiceId}/pdf`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `facture_${invoiceNumber.replace(/\//g, '-')}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            toast.error('Erreur lors du téléchargement du PDF');
        }
    };

    const handleExportCsv = async (period = 'month') => {
        try {
            const response = await api.get(`/accountant/export-csv?period=${period}`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `export_factures_${period}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Export CSV téléchargé');
        } catch (error) {
            toast.error('Erreur lors de l\'export CSV');
        }
    };

    const formatCurrency = (value) => {
        if (value === null || value === undefined) return '-';
        return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return format(new Date(dateStr), 'dd/MM/yyyy', { locale: fr });
    };

    // Stats
    const invoiceStats = {
        brouillon: invoices.filter(i => i.status === 'brouillon').length,
        en_verification: invoices.filter(i => i.status === 'en_verification').length,
        validee: invoices.filter(i => i.status === 'validee').length,
        emise: invoices.filter(i => i.status === 'emise').length,
        payee: invoices.filter(i => i.status === 'payee').length
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div className="space-y-6">
                    <Skeleton className="h-8 w-48" />
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
                    </div>
                    <Skeleton className="h-96" />
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                        <h1 className="text-xl sm:text-2xl font-outfit font-semibold text-slate-900">
                            Module Facturation
                        </h1>
                        <p className="text-slate-500 text-sm">
                            Gestion des factures avec workflow de validation
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleExportCsv('month')}>
                            <Download className="w-4 h-4 mr-1" />
                            <span className="hidden sm:inline">Export</span> CSV
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => {
                            setShowAccountantDialog(true);
                        }}>
                            <UserCheck className="w-4 h-4 mr-1" />
                            <span className="hidden sm:inline">Accès</span> Comptable
                        </Button>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                    <Card className="bg-slate-50">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs text-slate-500">Brouillons</p>
                                    <p className="text-2xl font-semibold">{invoiceStats.brouillon}</p>
                                </div>
                                <Clock className="w-8 h-8 text-slate-400" />
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-amber-50">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs text-amber-600">En vérification</p>
                                    <p className="text-2xl font-semibold text-amber-700">{invoiceStats.en_verification}</p>
                                </div>
                                <AlertTriangle className="w-8 h-8 text-amber-400" />
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-blue-50">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs text-blue-600">Validées</p>
                                    <p className="text-2xl font-semibold text-blue-700">{invoiceStats.validee}</p>
                                </div>
                                <UserCheck className="w-8 h-8 text-blue-400" />
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-indigo-50">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs text-indigo-600">Émises</p>
                                    <p className="text-2xl font-semibold text-indigo-700">{invoiceStats.emise}</p>
                                </div>
                                <Send className="w-8 h-8 text-indigo-400" />
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-green-50">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs text-green-600">Payées</p>
                                    <p className="text-2xl font-semibold text-green-700">{invoiceStats.payee}</p>
                                </div>
                                <CheckCircle className="w-8 h-8 text-green-400" />
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList>
                        <TabsTrigger value="invoices">
                            Factures ({invoices.length})
                        </TabsTrigger>
                        <TabsTrigger value="suggestions">
                            Suggestions ({suggestions.length})
                        </TabsTrigger>
                        {isAdminFull && (
                            <TabsTrigger value="config">
                                Configuration
                            </TabsTrigger>
                        )}
                        <TabsTrigger value="accountants">
                            Accès Comptable ({accountantAccesses.length})
                        </TabsTrigger>
                        {isAdminFull && (
                            <TabsTrigger value="history" className="text-amber-600">
                                <Bolt className="w-3 h-3 mr-1" />
                                Forçages ({forceHistory.length})
                            </TabsTrigger>
                        )}
                    </TabsList>

                    {/* Invoices Tab */}
                    <TabsContent value="invoices" className="mt-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>Liste des Factures</CardTitle>
                                <CardDescription>
                                    Workflow: Brouillon → En vérification → Validée → Émise → Payée
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                {invoices.length === 0 ? (
                                    <div className="text-center py-12 text-slate-500">
                                        <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                        <p>Aucune facture</p>
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Facture</TableHead>
                                                <TableHead>Montant</TableHead>
                                                <TableHead>Statut</TableHead>
                                                <TableHead>Actions</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {invoices.map((invoice) => {
                                                const statusConfig = STATUS_CONFIG[invoice.status] || STATUS_CONFIG.brouillon;
                                                const StatusIcon = statusConfig.icon;
                                                
                                                // Titre identifiable
                                                const invoiceTitle = invoice.invoice_label 
                                                    ? `${invoice.invoice_label} - ${invoice.prospect_name || 'N/A'}`
                                                    : `${invoice.invoice_number}`;
                                                
                                                const pipelineLabel = invoice.pipeline_type === 'easy_power' ? 'Courtage' : 
                                                                      invoice.pipeline_type === 'chantier' ? 'Travaux' : '';
                                                
                                                return (
                                                    <TableRow key={invoice.id} className="hover:bg-slate-50">
                                                        {/* Colonne Facture - Titre identifiable */}
                                                        <TableCell>
                                                            <div className="space-y-1">
                                                                <div className="flex items-center gap-2">
                                                                    <span className="font-mono text-xs text-slate-500">
                                                                        {invoice.invoice_number}
                                                                    </span>
                                                                    {pipelineLabel && (
                                                                        <Badge variant="outline" className={
                                                                            invoice.pipeline_type === 'easy_power' 
                                                                                ? 'text-amber-600 border-amber-300 text-xs' 
                                                                                : 'text-blue-600 border-blue-300 text-xs'
                                                                        }>
                                                                            {pipelineLabel}
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                                <div className="font-medium text-slate-900">
                                                                    {invoice.invoice_label || 'Facture'}
                                                                </div>
                                                                <div className="text-sm text-slate-600">
                                                                    {invoice.prospect_name || '-'}
                                                                </div>
                                                                <div className="flex items-center gap-4 text-xs text-slate-500 mt-1">
                                                                    <span>
                                                                        <strong>De:</strong> {invoice.issuer_name || '-'}
                                                                    </span>
                                                                    <span>→</span>
                                                                    <span>
                                                                        <strong>À:</strong> {invoice.payer_name || '-'}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </TableCell>
                                                        
                                                        {/* Colonne Montant */}
                                                        <TableCell>
                                                            <div className="space-y-1">
                                                                <div className="font-semibold text-lg">
                                                                    {formatCurrency(invoice.total_ttc)}
                                                                </div>
                                                                <div className="text-xs text-slate-500">
                                                                    HT: {formatCurrency(invoice.total_ht)} | TVA {invoice.tva_rate}%
                                                                </div>
                                                                <div className="text-xs text-slate-400">
                                                                    {formatDate(invoice.created_at)}
                                                                </div>
                                                            </div>
                                                        </TableCell>
                                                        
                                                        {/* Colonne Statut */}
                                                        <TableCell>
                                                            <Badge className={`${statusConfig.color} flex items-center gap-1 w-fit`}>
                                                                <StatusIcon className="w-3 h-3" />
                                                                {statusConfig.label}
                                                            </Badge>
                                                        </TableCell>
                                                        
                                                        {/* Colonne Actions */}
                                                        <TableCell>
                                                            <div className="flex flex-col gap-1">
                                                                {/* Bouton Voir */}
                                                                <Button 
                                                                    size="sm" 
                                                                    variant="outline"
                                                                    onClick={() => {
                                                                        setSelectedInvoice(invoice);
                                                                        setShowInvoiceViewDialog(true);
                                                                    }}
                                                                    className="w-full justify-start"
                                                                >
                                                                    <Eye className="w-3 h-3 mr-2" />
                                                                    Voir
                                                                </Button>
                                                                
                                                                {/* Actions de workflow - Full Admin seulement */}
                                                                {isAdminFull && (
                                                                    <>
                                                                        {invoice.status === 'brouillon' && (
                                                                            <Button 
                                                                                size="sm" 
                                                                                variant="outline"
                                                                                onClick={() => handleUpdateInvoiceStatus(invoice.id, 'en_verification')}
                                                                                className="w-full justify-start text-amber-600 border-amber-300 hover:bg-amber-50"
                                                                            >
                                                                                <Send className="w-3 h-3 mr-2" />
                                                                                Soumettre
                                                                            </Button>
                                                                        )}
                                                                        {invoice.status === 'en_verification' && (
                                                                            <Button 
                                                                                size="sm" 
                                                                                variant="outline"
                                                                                onClick={() => handleUpdateInvoiceStatus(invoice.id, 'validee')}
                                                                                className="w-full justify-start text-blue-600 border-blue-300 hover:bg-blue-50"
                                                                            >
                                                                                <CheckCircle className="w-3 h-3 mr-2" />
                                                                                Valider
                                                                            </Button>
                                                                        )}
                                                                        {invoice.status === 'validee' && (
                                                                            <Button 
                                                                                size="sm" 
                                                                                variant="outline"
                                                                                onClick={() => handleUpdateInvoiceStatus(invoice.id, 'emise')}
                                                                                className="w-full justify-start text-purple-600 border-purple-300 hover:bg-purple-50"
                                                                            >
                                                                                <ArrowUpRight className="w-3 h-3 mr-2" />
                                                                                Émettre
                                                                            </Button>
                                                                        )}
                                                                        {invoice.status === 'emise' && (
                                                                            <Button 
                                                                                size="sm" 
                                                                                variant="outline"
                                                                                onClick={() => handleUpdateInvoiceStatus(invoice.id, 'payee')}
                                                                                className="w-full justify-start text-green-600 border-green-300 hover:bg-green-50"
                                                                            >
                                                                                <Banknote className="w-3 h-3 mr-2" />
                                                                                Marquer payée
                                                                            </Button>
                                                                        )}
                                                                    </>
                                                                )}
                                                                
                                                                {/* Télécharger PDF */}
                                                                <Button 
                                                                    size="sm" 
                                                                    variant="ghost"
                                                                    onClick={() => handleDownloadPDF(invoice.id)}
                                                                    className="w-full justify-start text-slate-600"
                                                                >
                                                                    <Download className="w-3 h-3 mr-2" />
                                                                    PDF
                                                                </Button>
                                                            </div>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Suggestions Tab */}
                    <TabsContent value="suggestions" className="mt-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>Suggestions de Facturation</CardTitle>
                                <CardDescription>
                                    Courtage: 5 jours après "Contrat signé" | Travaux d’optimisation: 21 jours après "Validé"
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                {suggestions.length === 0 ? (
                                    <div className="text-center py-12 text-slate-500">
                                        <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                        <p>Aucune suggestion de facturation</p>
                                        <p className="text-sm mt-2">Les suggestions apparaissent quand les dossiers atteignent les étapes requises</p>
                                    </div>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Prospect</TableHead>
                                                <TableHead>Pipeline</TableHead>
                                                <TableHead>Type facture</TableHead>
                                                <TableHead>Depuis</TableHead>
                                                <TableHead>Montant</TableHead>
                                                <TableHead>Émetteur</TableHead>
                                                <TableHead>Payeur</TableHead>
                                                <TableHead>TVA</TableHead>
                                                <TableHead>Statut</TableHead>
                                                <TableHead>Action</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {suggestions.map((suggestion, idx) => (
                                                <TableRow key={idx}>
                                                    <TableCell className="font-medium">
                                                        {suggestion.prospect_name}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge variant="outline" className={
                                                            suggestion.pipeline_type === 'easy_power' 
                                                                ? 'text-amber-600 border-amber-300' 
                                                                : 'text-blue-600 border-blue-300'
                                                        }>
                                                            {suggestion.pipeline_type === 'easy_power' ? (
                                                                <><Zap className="w-3 h-3 mr-1" /> Courtage</>
                                                            ) : (
                                                                <><HardHat className="w-3 h-3 mr-1" /> Travaux</>
                                                            )}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className={
                                                            suggestion.invoice_label === 'Part Valnexo' ? 'text-blue-600 font-medium' :
                                                            suggestion.invoice_label === 'Commission VP' ? 'text-amber-600 font-medium' :
                                                            suggestion.invoice_label === 'Commission Apporteur' ? 'text-green-600 font-medium' :
                                                            ''
                                                        }>
                                                            {suggestion.invoice_label || suggestion.stage}
                                                        </span>
                                                        {suggestion.boost_included > 0 && (
                                                            <span className="text-xs text-purple-500 ml-1">
                                                                (+boost {formatCurrency(suggestion.boost_included)})
                                                            </span>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className={suggestion.days_since >= suggestion.delay_days ? 'text-green-600 font-medium' : 'text-slate-500'}>
                                                            {suggestion.days_since}j
                                                        </span>
                                                        <span className="text-xs text-slate-400 ml-1">
                                                            /{suggestion.delay_days}j
                                                        </span>
                                                    </TableCell>
                                                    <TableCell className="font-semibold">
                                                        {formatCurrency(suggestion.amount)}
                                                    </TableCell>
                                                    <TableCell className="text-sm">
                                                        {suggestion.issuer_name}
                                                    </TableCell>
                                                    <TableCell className="text-sm">
                                                        {isAdminFull ? (
                                                            <select 
                                                                className="text-xs border rounded px-1 py-0.5"
                                                                value={selectedPayers[getSuggestionKey(suggestion, idx)] || suggestion.payer_type}
                                                                onChange={(e) => handlePayerChange(getSuggestionKey(suggestion, idx), e.target.value)}
                                                                title="Full Admin: choisir le payeur"
                                                            >
                                                                <option value="valnexo">Valnexo</option>
                                                                <option value="at_service">AT Service</option>
                                                                {suggestion.payer_type === 'vp' && (
                                                                    <option value="vp">{suggestion.payer_name || 'VP'}</option>
                                                                )}
                                                                <option value="50_50">50/50</option>
                                                                {availablePayers.filter(p => p.type === 'custom').map(payer => (
                                                                    <option key={payer.id} value={payer.id}>{payer.name}</option>
                                                                ))}
                                                            </select>
                                                        ) : suggestion.payer_name ? (
                                                            <span className={
                                                                suggestion.payer_type === 'valnexo' ? 'text-blue-600 font-medium' :
                                                                suggestion.payer_type === 'at_service' ? 'text-purple-600 font-medium' :
                                                                suggestion.payer_type === 'vp' ? 'text-amber-600 font-medium' :
                                                                ''
                                                            }>
                                                                {suggestion.payer_name}
                                                            </span>
                                                        ) : (
                                                            <span className="text-slate-400">-</span>
                                                        )}
                                                    </TableCell>
                                                    {/* Colonne TVA */}
                                                    <TableCell className="text-sm">
                                                        {isAdminFull ? (
                                                            <select 
                                                                className="text-xs border rounded px-1 py-0.5"
                                                                value={selectedTvas[getSuggestionKey(suggestion, idx)] || 'tva_20'}
                                                                onChange={(e) => handleTvaChange(getSuggestionKey(suggestion, idx), e.target.value)}
                                                                title="Modifier le régime TVA"
                                                            >
                                                                <option value="tva_20">TVA 20%</option>
                                                                <option value="tva_10">TVA 10%</option>
                                                                <option value="tva_5_5">TVA 5,5%</option>
                                                                <option value="exonere">Exonéré</option>
                                                            </select>
                                                        ) : (
                                                            <span className="text-slate-600">20%</span>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {suggestion.suggested ? (
                                                            <Badge className="bg-green-100 text-green-700">
                                                                <CheckCircle className="w-3 h-3 mr-1" />
                                                                Prêt
                                                            </Badge>
                                                        ) : (
                                                            <Badge className="bg-amber-100 text-amber-700">
                                                                <Clock className="w-3 h-3 mr-1" />
                                                                En attente
                                                            </Badge>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex gap-1">
                                                            <Button 
                                                                size="sm" 
                                                                variant={suggestion.suggested ? "default" : "outline"}
                                                                disabled={!suggestion.suggested || suggestion.amount <= 0}
                                                                onClick={() => handleCreateFromSuggestion(
                                                                    suggestion, 
                                                                    selectedPayers[getSuggestionKey(suggestion, idx)],
                                                                    selectedTvas[getSuggestionKey(suggestion, idx)]
                                                                )}
                                                                className={suggestion.suggested ? "bg-green-600 hover:bg-green-700" : ""}
                                                            >
                                                                <Plus className="w-3 h-3 mr-1" />
                                                                Créer
                                                            </Button>
                                                            {/* Bouton Forcer - Full Admin uniquement, si pas encore prêt */}
                                                            {isAdminFull && !suggestion.suggested && suggestion.amount > 0 && (
                                                                <Button 
                                                                    size="sm" 
                                                                    variant="outline"
                                                                    className="text-amber-600 border-amber-300 hover:bg-amber-50"
                                                                    onClick={() => handleOpenForceDialog(suggestion)}
                                                                    title="Forcer la facturation (Full Admin)"
                                                                >
                                                                    <Bolt className="w-3 h-3 mr-1" />
                                                                    Forcer
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Config Tab (Admin Full only) */}
                    {isAdminFull && (
                        <TabsContent value="config" className="mt-6">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Settings className="w-5 h-5" />
                                        Configuration Facturation par Utilisateur
                                    </CardTitle>
                                    <CardDescription>
                                        Définissez la société payeuse et le régime TVA pour chaque VP/Apporteur
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    {billingConfigs.length === 0 ? (
                                        <div className="text-center py-12 text-slate-500">
                                            <p>Aucune configuration. Les configurations sont créées automatiquement lors de la première facture.</p>
                                        </div>
                                    ) : (
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Utilisateur</TableHead>
                                                    <TableHead>Rôle</TableHead>
                                                    <TableHead>Société Payeuse</TableHead>
                                                    <TableHead>Régime TVA</TableHead>
                                                    <TableHead>Facturation Directe</TableHead>
                                                    <TableHead>Préfixe Facture</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {billingConfigs.map((config) => (
                                                    <TableRow key={config.user_id}>
                                                        <TableCell className="font-medium">
                                                            {config.user_name}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Badge variant="outline">
                                                                {config.user_role}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Select
                                                                value={config.default_payer}
                                                                onValueChange={(v) => handleUpdateBillingConfig(config.user_id, { default_payer: v })}
                                                            >
                                                                <SelectTrigger className="w-32">
                                                                    <SelectValue />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {PAYER_OPTIONS.map(opt => (
                                                                        <SelectItem key={opt.value} value={opt.value}>
                                                                            {opt.label}
                                                                        </SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Select
                                                                value={config.tva_regime}
                                                                onValueChange={(v) => handleUpdateBillingConfig(config.user_id, { tva_regime: v })}
                                                            >
                                                                <SelectTrigger className="w-40">
                                                                    <SelectValue />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {TVA_OPTIONS.map(opt => (
                                                                        <SelectItem key={opt.value} value={opt.value}>
                                                                            {opt.label}
                                                                        </SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                        </TableCell>
                                                        <TableCell>
                                                            <input
                                                                type="checkbox"
                                                                checked={config.can_invoice_direct}
                                                                onChange={(e) => handleUpdateBillingConfig(config.user_id, { can_invoice_direct: e.target.checked })}
                                                                className="w-4 h-4"
                                                            />
                                                        </TableCell>
                                                        <TableCell className="font-mono text-sm">
                                                            {config.invoice_prefix || '-'}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    )}
                                </CardContent>
                            </Card>
                        </TabsContent>
                    )}

                    {/* Accountants Tab */}
                    <TabsContent value="accountants" className="mt-6">
                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between">
                                <div>
                                    <CardTitle className="flex items-center gap-2">
                                        <UserCheck className="w-5 h-5" />
                                        Accès Comptable Délégué
                                    </CardTitle>
                                    <CardDescription>
                                        Donnez un accès lecture seule à votre comptable
                                    </CardDescription>
                                </div>
                                <Button onClick={() => setShowAccountantDialog(true)}>
                                    <Plus className="w-4 h-4 mr-2" />
                                    Nouvel accès
                                </Button>
                            </CardHeader>
                            <CardContent>
                                {accountantAccesses.length === 0 ? (
                                    <div className="text-center py-12 text-slate-500">
                                        <UserCheck className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                        <p>Aucun accès comptable configuré</p>
                                        <p className="text-sm mt-2">Créez un accès pour permettre à votre comptable de consulter vos factures</p>
                                    </div>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Comptable</TableHead>
                                                <TableHead>Email</TableHead>
                                                <TableHead>Société</TableHead>
                                                <TableHead>Statut</TableHead>
                                                <TableHead>Dernière connexion</TableHead>
                                                <TableHead>Actions</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {accountantAccesses.map((access) => (
                                                <TableRow key={access.id}>
                                                    <TableCell className="font-medium">
                                                        {access.name}
                                                    </TableCell>
                                                    <TableCell className="text-sm">
                                                        {access.email}
                                                    </TableCell>
                                                    <TableCell className="text-sm">
                                                        {access.owner_company_name}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge className={access.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                                                            {access.is_active ? 'Actif' : 'Désactivé'}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell className="text-sm text-slate-500">
                                                        {access.last_login ? formatDate(access.last_login) : 'Jamais'}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Button 
                                                            size="sm" 
                                                            variant="outline"
                                                            onClick={() => handleToggleAccountantAccess(access.id)}
                                                        >
                                                            {access.is_active ? 'Désactiver' : 'Activer'}
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Force History Tab (Admin Full only) */}
                    {isAdminFull && (
                        <TabsContent value="history" className="mt-6">
                            <Card>
                                <CardHeader>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <CardTitle className="flex items-center gap-2">
                                                <History className="w-5 h-5" />
                                                Historique des Facturations Forcées
                                            </CardTitle>
                                            <CardDescription>
                                                Traçabilité des facturations manuelles (qui, quand, pourquoi)
                                            </CardDescription>
                                        </div>
                                        {forceHistory.length > 0 && (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                                onClick={async () => {
                                                    if (confirm('Supprimer TOUT l\'historique des facturations forcées ?')) {
                                                        try {
                                                            await api.delete('/billing/force-invoice/history');
                                                            toast.success('Historique supprimé');
                                                            setForceHistory([]);
                                                        } catch (err) {
                                                            toast.error('Erreur lors de la suppression');
                                                        }
                                                    }
                                                }}
                                                data-testid="delete-all-force-history"
                                            >
                                                <Trash2 className="h-4 w-4 mr-2" />
                                                Tout supprimer
                                            </Button>
                                        )}
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    {forceHistory.length === 0 ? (
                                        <div className="text-center py-12 text-slate-500">
                                            <Bolt className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                            <p>Aucune facturation forcée</p>
                                            <p className="text-sm mt-2">L'historique des actions manuelles apparaîtra ici</p>
                                        </div>
                                    ) : (
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Date</TableHead>
                                                    <TableHead>Prospect</TableHead>
                                                    <TableHead>Pipeline</TableHead>
                                                    <TableHead>Montant</TableHead>
                                                    <TableHead>Forcé par</TableHead>
                                                    <TableHead>Raison</TableHead>
                                                    <TableHead className="w-16">Actions</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {forceHistory.map((item) => (
                                                    <TableRow key={item.id}>
                                                        <TableCell className="text-sm">
                                                            {formatDate(item.forced_at)}
                                                        </TableCell>
                                                        <TableCell className="font-medium">
                                                            {item.prospect_name}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Badge variant="outline" className={
                                                                item.pipeline_type === 'easy_power' 
                                                                    ? 'text-amber-600 border-amber-300' 
                                                                    : 'text-blue-600 border-blue-300'
                                                            }>
                                                                {item.pipeline_type === 'easy_power' ? 'Courtage' : 'Travaux d’optimisation'}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell className="font-semibold">
                                                            {formatCurrency(item.amount)}
                                                        </TableCell>
                                                        <TableCell className="text-sm">
                                                            {item.forced_by_name}
                                                        </TableCell>
                                                        <TableCell className="text-sm text-slate-600 max-w-xs truncate" title={item.reason}>
                                                            {item.reason}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                                                onClick={async () => {
                                                                    if (confirm('Supprimer cette entrée ?')) {
                                                                        try {
                                                                            await api.delete(`/billing/force-invoice/history/${item.id}`);
                                                                            toast.success('Entrée supprimée');
                                                                            setForceHistory(prev => prev.filter(h => h.id !== item.id));
                                                                        } catch (err) {
                                                                            toast.error('Erreur lors de la suppression');
                                                                        }
                                                                    }
                                                                }}
                                                                data-testid={`delete-force-history-${item.id}`}
                                                            >
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    )}
                                </CardContent>
                            </Card>
                        </TabsContent>
                    )}
                </Tabs>
            </div>

            {/* Dialog: Create Accountant Access */}
            <Dialog open={showAccountantDialog} onOpenChange={setShowAccountantDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Créer un accès comptable</DialogTitle>
                        <DialogDescription>
                            Votre comptable pourra consulter vos factures en lecture seule
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Nom du comptable / Cabinet</Label>
                            <Input
                                value={accountantForm.name}
                                onChange={(e) => setAccountantForm(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="Cabinet Expert Comptable"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Email de connexion</Label>
                            <Input
                                type="email"
                                value={accountantForm.email}
                                onChange={(e) => setAccountantForm(prev => ({ ...prev, email: e.target.value }))}
                                placeholder="comptable@cabinet.fr"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Mot de passe</Label>
                            <Input
                                type="password"
                                value={accountantForm.password}
                                onChange={(e) => setAccountantForm(prev => ({ ...prev, password: e.target.value }))}
                                placeholder="••••••••"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAccountantDialog(false)}>
                            Annuler
                        </Button>
                        <Button onClick={handleCreateAccountantAccess}>
                            Créer l'accès
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog: Force Invoice */}
            <Dialog open={showForceDialog} onOpenChange={setShowForceDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-amber-600">
                            <Bolt className="w-5 h-5" />
                            Forcer la Facturation
                        </DialogTitle>
                        <DialogDescription>
                            Action réservée aux Full Admin - Facturation sans délai
                        </DialogDescription>
                    </DialogHeader>
                    
                    {selectedForce && (
                        <div className="space-y-4 py-4">
                            {/* Info dossier */}
                            <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-slate-500">Prospect</span>
                                    <span className="font-medium">{selectedForce.prospect_name}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-slate-500">Pipeline</span>
                                    <Badge variant="outline" className={
                                        selectedForce.pipeline_type === 'easy_power' 
                                            ? 'text-amber-600 border-amber-300' 
                                            : 'text-blue-600 border-blue-300'
                                    }>
                                        {selectedForce.pipeline_type === 'easy_power' ? 'Courtage' : 'Travaux d’optimisation'}
                                    </Badge>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-slate-500">Montant</span>
                                    <span className="font-semibold">{formatCurrency(selectedForce.amount)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-slate-500">Délai normal</span>
                                    <span className="text-amber-600">
                                        {selectedForce.days_since} / {selectedForce.delay_days} jours
                                    </span>
                                </div>
                            </div>

                            {/* Avertissement */}
                            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                <div className="flex gap-2">
                                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                                    <div className="text-sm text-amber-800">
                                        <p className="font-medium">Action exceptionnelle</p>
                                        <p>Cette action permet de facturer avant le délai normal. Elle sera historisée avec votre identité et la raison.</p>
                                    </div>
                                </div>
                            </div>

                            {/* Raison obligatoire */}
                            <div className="space-y-2">
                                <Label className="text-red-600">Raison du forçage *</Label>
                                <Textarea
                                    value={forceForm.reason}
                                    onChange={(e) => setForceForm(prev => ({ ...prev, reason: e.target.value }))}
                                    placeholder="Expliquez pourquoi cette facturation doit être forcée (min. 10 caractères)..."
                                    rows={3}
                                    className="resize-none"
                                />
                                <p className="text-xs text-slate-500">
                                    {forceForm.reason.length}/10 caractères minimum
                                </p>
                            </div>

                            {/* Confirmation explicite */}
                            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                                <input
                                    type="checkbox"
                                    id="confirm-force"
                                    checked={forceForm.confirm}
                                    onChange={(e) => setForceForm(prev => ({ ...prev, confirm: e.target.checked }))}
                                    className="w-4 h-4 text-red-600"
                                />
                                <label htmlFor="confirm-force" className="text-sm text-red-800">
                                    Je confirme vouloir forcer cette facturation
                                </label>
                            </div>
                        </div>
                    )}

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowForceDialog(false)}>
                            Annuler
                        </Button>
                        <Button 
                            onClick={handleForceInvoice}
                            disabled={forceLoading || !forceForm.confirm || forceForm.reason.length < 10}
                            className="bg-amber-600 hover:bg-amber-700"
                        >
                            {forceLoading ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Bolt className="w-4 h-4 mr-2" />
                            )}
                            Forcer la facturation
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog: View Invoice Detail */}
            <Dialog open={showInvoiceViewDialog} onOpenChange={setShowInvoiceViewDialog}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileText className="w-5 h-5" />
                            Détail de la Facture
                        </DialogTitle>
                    </DialogHeader>
                    
                    {selectedInvoice && (
                        <div className="space-y-6">
                            {/* En-tête avec titre identifiable */}
                            <div className="bg-slate-50 p-4 rounded-lg border">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <p className="text-xs text-slate-500 font-mono">{selectedInvoice.invoice_number}</p>
                                        <h3 className="text-xl font-bold text-slate-900 mt-1">
                                            {selectedInvoice.invoice_label || 'Facture'}
                                        </h3>
                                        <p className="text-lg text-slate-700">
                                            {selectedInvoice.prospect_name || '-'}
                                        </p>
                                        <Badge variant="outline" className={`mt-2 ${
                                            selectedInvoice.pipeline_type === 'easy_power' 
                                                ? 'text-amber-600 border-amber-300' 
                                                : 'text-blue-600 border-blue-300'
                                        }`}>
                                            {selectedInvoice.pipeline_type === 'easy_power' ? 'Courtage' : 'Travaux d’optimisation'}
                                        </Badge>
                                    </div>
                                    <div className="text-right">
                                        <Badge className={`${STATUS_CONFIG[selectedInvoice.status]?.color || 'bg-slate-100'} text-lg px-3 py-1`}>
                                            {STATUS_CONFIG[selectedInvoice.status]?.label || selectedInvoice.status}
                                        </Badge>
                                    </div>
                                </div>
                            </div>

                            {/* Parties */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                                    <p className="text-xs text-blue-600 font-semibold uppercase">Émetteur</p>
                                    <p className="text-lg font-medium text-slate-900 mt-1">
                                        {selectedInvoice.issuer_name || '-'}
                                    </p>
                                    <p className="text-sm text-slate-500">
                                        {selectedInvoice.issuer_type === 'valnexo' ? 'Valnexo' : 
                                         selectedInvoice.issuer_type === 'vp' ? 'VP' : 
                                         selectedInvoice.issuer_type === 'apporteur' ? 'Apporteur' : '-'}
                                    </p>
                                </div>
                                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                                    <p className="text-xs text-purple-600 font-semibold uppercase">Payeur</p>
                                    <p className="text-lg font-medium text-slate-900 mt-1">
                                        {selectedInvoice.payer_name || '-'}
                                    </p>
                                    <p className="text-sm text-slate-500">
                                        {selectedInvoice.payer_type === 'valnexo' ? 'Système' : 
                                         selectedInvoice.payer_type === 'at_service' ? 'Système' : 
                                         selectedInvoice.payer_type === 'vp' ? 'VP' : 
                                         selectedInvoice.payer_type === '50_50' ? '50/50' : 'Payeur personnalisé'}
                                    </p>
                                </div>
                            </div>

                            {/* Montants */}
                            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                                <div className="grid grid-cols-3 gap-4 text-center">
                                    <div>
                                        <p className="text-xs text-slate-500">Montant HT</p>
                                        <p className="text-xl font-bold text-slate-900">
                                            {formatCurrency(selectedInvoice.total_ht)}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-500">
                                            TVA ({selectedInvoice.tva_rate}%)
                                            {isAdminFull && (selectedInvoice.status === 'brouillon' || selectedInvoice.status === 'en_verification') && (
                                                <select 
                                                    className="ml-2 text-xs border rounded px-1 py-0.5 cursor-pointer"
                                                    value={selectedInvoice.tva_regime || 'tva_20'}
                                                    onChange={async (e) => {
                                                        try {
                                                            const res = await api.put(`/invoices/v2/${selectedInvoice.id}/tva`, { tva_regime: e.target.value });
                                                            toast.success(`TVA modifiée: ${res.data.tva_rate}%`);
                                                            loadData();
                                                            // Mettre à jour la facture sélectionnée
                                                            setSelectedInvoice(prev => ({
                                                                ...prev,
                                                                tva_regime: res.data.tva_regime,
                                                                tva_rate: res.data.tva_rate,
                                                                tva_amount: res.data.tva_amount,
                                                                total_ttc: res.data.total_ttc
                                                            }));
                                                        } catch (error) {
                                                            toast.error(error.response?.data?.detail || 'Erreur');
                                                        }
                                                    }}
                                                    title="Modifier la TVA"
                                                >
                                                    <option value="tva_20">20%</option>
                                                    <option value="tva_10">10%</option>
                                                    <option value="tva_5_5">5,5%</option>
                                                    <option value="exonere">0%</option>
                                                </select>
                                            )}
                                        </p>
                                        <p className="text-xl font-bold text-slate-700">
                                            {formatCurrency(selectedInvoice.tva_amount)}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-green-600 font-semibold">Total TTC</p>
                                        <p className="text-2xl font-bold text-green-700">
                                            {formatCurrency(selectedInvoice.total_ttc)}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Dates */}
                            <div className="grid grid-cols-3 gap-4 text-sm">
                                <div>
                                    <p className="text-slate-500">Créée le</p>
                                    <p className="font-medium">{formatDate(selectedInvoice.created_at)}</p>
                                </div>
                                {selectedInvoice.issued_at && (
                                    <div>
                                        <p className="text-slate-500">Émise le</p>
                                        <p className="font-medium">{formatDate(selectedInvoice.issued_at)}</p>
                                    </div>
                                )}
                                {selectedInvoice.paid_at && (
                                    <div>
                                        <p className="text-slate-500">Payée le</p>
                                        <p className="font-medium text-green-600">{formatDate(selectedInvoice.paid_at)}</p>
                                    </div>
                                )}
                            </div>

                            {/* Notes */}
                            {selectedInvoice.notes && (
                                <div className="p-3 bg-slate-100 rounded-lg">
                                    <p className="text-xs text-slate-500 mb-1">Notes</p>
                                    <p className="text-sm">{selectedInvoice.notes}</p>
                                </div>
                            )}

                            {/* Actions Full Admin */}
                            {isAdminFull && (
                                <div className="flex gap-2 pt-4 border-t">
                                    {selectedInvoice.status === 'brouillon' && (
                                        <Button 
                                            onClick={() => {
                                                handleUpdateInvoiceStatus(selectedInvoice.id, 'en_verification');
                                                setShowInvoiceViewDialog(false);
                                            }}
                                            className="bg-amber-600 hover:bg-amber-700"
                                        >
                                            <Send className="w-4 h-4 mr-2" />
                                            Soumettre pour vérification
                                        </Button>
                                    )}
                                    {selectedInvoice.status === 'en_verification' && (
                                        <Button 
                                            onClick={() => {
                                                handleUpdateInvoiceStatus(selectedInvoice.id, 'validee');
                                                setShowInvoiceViewDialog(false);
                                            }}
                                            className="bg-blue-600 hover:bg-blue-700"
                                        >
                                            <CheckCircle className="w-4 h-4 mr-2" />
                                            Valider la facture
                                        </Button>
                                    )}
                                    {selectedInvoice.status === 'validee' && (
                                        <Button 
                                            onClick={() => {
                                                handleUpdateInvoiceStatus(selectedInvoice.id, 'emise');
                                                setShowInvoiceViewDialog(false);
                                            }}
                                            className="bg-purple-600 hover:bg-purple-700"
                                        >
                                            <ArrowUpRight className="w-4 h-4 mr-2" />
                                            Émettre la facture
                                        </Button>
                                    )}
                                    {selectedInvoice.status === 'emise' && (
                                        <Button 
                                            onClick={() => {
                                                handleUpdateInvoiceStatus(selectedInvoice.id, 'payee');
                                                setShowInvoiceViewDialog(false);
                                            }}
                                            className="bg-green-600 hover:bg-green-700"
                                        >
                                            <Banknote className="w-4 h-4 mr-2" />
                                            Marquer comme payée
                                        </Button>
                                    )}
                                    <Button 
                                        variant="outline"
                                        onClick={() => handleDownloadPDF(selectedInvoice.id)}
                                    >
                                        <Download className="w-4 h-4 mr-2" />
                                        Télécharger PDF
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </DashboardLayout>
    );
}
